import fetch from "cross-fetch";
import { IAM_COOKIE_NAME, IAM_API_URL, PARSIVE_ENABLED } from "../../settings/config";
import { setCookie, defaultIamCookieAttributes } from "../../utils/cookie";
import { sendUserRegisterEvent } from "../../utils/analytics";
import { addNotificationWithTimeout } from "../notifications/operations";
import { authRequest } from "../authorizedRequests";
import { getRefreshToken, getLoggedInUserGuid, getLoggedInUserSubscription } from "./selectors";
import { logout as logoutUser } from "../../iam/logout";
import {
    authenticationInProgress,
    authenticationSuccessful,
    authenticationFailed,
    userIsLoading,
    getUser,
    userIsNotAvailable,
    registrationInProgress,
    registrationFailed,
    registrationSuccessful,
    registrationAndLoginIsFinished,
    deleteUserInProgress,
    deleteUserSuccessful,
    deleteUserFailed,
    passwordResetRequestInProgress,
    passwordResetRequestSuccess,
    passwordResetRequestFailed,
    changeUserNameInProgress,
    changeUserNameSuccessful,
    changeUserNameFailed,
    changePasswordInProgress,
    changePasswordSuccessful,
    changePasswordFailed,
    confirmPasswordInProgress,
    confirmPasswordSuccessful,
    confirmPasswordFailed,
    updateAccessTokenFailed,
    updateAccessTokenInProgress,
    updateAccessTokenSuccess,
    clearIamState,
    updateRequiredExperiencesInProgress,
    updateRequiredExperiencesSuccessful,
    updateRequiredExperiencesFailed,
    createOrganizationFailed,
    createOrganizationInProgress,
    createOrganizationSuccessful,
    getOrganizationInProgress,
    getOrganizationSuccessful,
    getOrganizationFailed,
    updateOrganizationInProgress,
    updateOrganizationSuccessful,
    updateOrganizationFailed,
    deleteTeamMemberFailed,
    deleteTeamMemberInProgress,
    deleteTeamMemberSuccessful
} from "./creators";

export const authenticate = (email, password, recaptchaToken, shouldRememberMe, onError, navigate) => {
    const userEmail = email.toLowerCase();

    return (dispatch, getState) => {
        dispatch(clearIamState());
        dispatch(authenticationInProgress());

        return fetch(`${IAM_API_URL}/auth`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                user_name: userEmail,
                password: password,
                remember_me: shouldRememberMe,
                recaptcha_token: recaptchaToken
            })
        })
            .then((response) => {
                if (response.status === 201) {
                    return response.json();
                } else {
                    const message =
                        response.status === 401
                            ? "Invalid email or password!"
                            : "Something went wrong during authentication";
                    throw new Error(message);
                }
            })
            .then((responseBody) => {
                const accessToken = responseBody["access_token"];
                const refreshToken = responseBody["refresh_token"];

                if (accessToken && refreshToken) {
                    const serializedTokens = JSON.stringify({ access_token: accessToken, refresh_token: refreshToken });
                    setCookie(IAM_COOKIE_NAME, serializedTokens, defaultIamCookieAttributes);

                    dispatch(authenticationSuccessful(accessToken, refreshToken));

                    const subscription = getLoggedInUserSubscription(getState());
                    navigate(subscription && subscription["plan_code"] ? "/dashboard" : "/free-trial-setup");
                } else {
                    throw new Error("Something went wrong during authentication");
                }
            })
            .catch((error) => {
                dispatch(authenticationFailed());
                dispatch(addNotificationWithTimeout("error", error.message));
                onError();
            });
    };
};

export const getLoggedInUser = () => {
    return (dispatch, getState) => {
        dispatch(userIsLoading());

        const userGuid = getLoggedInUserGuid(getState());
        const apiUrl = `${IAM_API_URL}/user/${userGuid}`;

        authRequest(apiUrl, dispatch, getState)
            .then((response) => {
                dispatch(getUser(response));

                if (PARSIVE_ENABLED) {
                    const subscription = getLoggedInUserSubscription(getState());
                    // The loader stub in index.html creates window.parsive, but
                    // guard anyway: without it this would throw and lose the
                    // user response with it.
                    window.parsive = window.parsive || {};
                    window.parsive.context = {
                        user_id: response.guid,
                        name: `${response.f_name} ${response.l_name}`,
                        email: response.email,
                        plan: subscription ? subscription.plan_code : null,
                    };
                }
            })
            .catch((_) => {
                dispatch(userIsNotAvailable());
                dispatch(addNotificationWithTimeout("error", "Cannot get user data!"));

                dispatch(logoutUser());
            });
    };
};

export const register = (email, firstName, lastName, password, token, onError, navigate) => {
    const userEmail = email.toLowerCase();

    return (dispatch) => {
        dispatch(clearIamState());
        dispatch(registrationInProgress());

        fetch(`${IAM_API_URL}/user`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                u_name: userEmail,
                f_name: firstName,
                l_name: lastName,
                pw: password,
                email: userEmail,
                recaptcha_token: token,
                locale: "en_US",
                timezone: "America/New_York"
            })
        })
            .then((response) => {
                if (response.status === 201) {
                    return response.json();
                } else {
                    let message = "Something went wrong during registration";

                    if (response.status === 409) {
                        message = "Email address already in use!";
                    }

                    if (response.status === 400) {
                        message = "Invalid email address!";
                    }

                    throw Error(message);
                }
            })
            .then((responseBody) => {
                const userGuid = responseBody["guid"];
                const accessToken = responseBody["access_token"];
                const refreshToken = responseBody["refresh_token"];

                sendUserRegisterEvent(userGuid);

                if (accessToken && refreshToken) {
                    const serializedTokens = JSON.stringify({ access_token: accessToken, refresh_token: refreshToken });
                    setCookie(IAM_COOKIE_NAME, serializedTokens, defaultIamCookieAttributes);

                    dispatch(authenticationSuccessful(accessToken, refreshToken));
                    dispatch(registrationSuccessful());
                    dispatch(registrationAndLoginIsFinished());
                    navigate("/free-trial-setup");
                }
            })
            .catch((error) => {
                dispatch(registrationFailed());
                dispatch(registrationAndLoginIsFinished());
                dispatch(addNotificationWithTimeout("error", error.message));
                onError();
            });
    };
};

export const resetPasswordRequest = (email) => {
    const userEmail = email.toLowerCase();

    return (dispatch) => {
        dispatch(passwordResetRequestInProgress());
        fetch(`${IAM_API_URL}/user/reset-password`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                u_name: userEmail
            })
        })
            .then((response) => {
                if (response.status === 200) {
                    dispatch(passwordResetRequestSuccess());
                    dispatch(addNotificationWithTimeout("success", "A reset password email was sent to your address!"));
                } else {
                    throw new Error("We weren't able to identify you given the information provided");
                }
            })
            .catch((error) => {
                dispatch(passwordResetRequestFailed());
                dispatch(addNotificationWithTimeout("error", error.message));
            });
    };
};

export const resetPassword = (token, password, navigate) => {
    return (dispatch) => {
        dispatch(changePasswordInProgress());
        fetch(`${IAM_API_URL}/user/reset-password`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                token: token,
                pw: password
            })
        })
            .then((response) => {
                if (response.status === 200) {
                    dispatch(changePasswordSuccessful());
                    dispatch(addNotificationWithTimeout("success", "You successfully changed your password!"));
                    return response.json();
                } else {
                    throw new Error("This token was already used!");
                }
            })
            .then((body) => {
                const iam = {
                    access_token: body["access_token"],
                    refresh_token: body["refresh_token"]
                };

                const serializedIAM = JSON.stringify(iam);

                if (iam.access_token && iam.refresh_token) {
                    setCookie(IAM_COOKIE_NAME, serializedIAM, defaultIamCookieAttributes);
                }
                dispatch(authenticationSuccessful(body.access_token, body.refresh_token));
                navigate("/");
            })
            .catch((error) => {
                dispatch(changePasswordFailed());
                dispatch(addNotificationWithTimeout("error", error.message));
            });
    };
};

export const changeUserName = (firstName, lastName) => {
    return (dispatch, getState) => {
        dispatch(changeUserNameInProgress());

        const userGuid = getLoggedInUserGuid(getState());
        const apiUrl = `${IAM_API_URL}/user/${userGuid}`;

        authRequest(apiUrl, dispatch, getState, {
            method: "PATCH",
            body: JSON.stringify({
                f_name: firstName,
                l_name: lastName
            })
        })
            .then((_) => {
                dispatch(changeUserNameSuccessful());
                dispatch(addNotificationWithTimeout("success", "Your account was successfully updated!"));
                dispatch(getLoggedInUser());
            })
            .catch((_) => {
                dispatch(changeUserNameFailed());
                dispatch(addNotificationWithTimeout("error", "Cannot update your account"));
            });
    };
};

export const changePassword = (newPassword) => {
    return (dispatch, getState) => {
        dispatch(changePasswordInProgress());

        const userGuid = getLoggedInUserGuid(getState());
        const apiUrl = `${IAM_API_URL}/user/${userGuid}`;

        authRequest(apiUrl, dispatch, getState, {
            method: "PATCH",
            body: JSON.stringify({
                pw: newPassword
            })
        })
            .then((_) => {
                dispatch(changePasswordSuccessful());
                dispatch(addNotificationWithTimeout("success", "Your account was successfully updated!"));
                dispatch(getLoggedInUser());
            })
            .catch((_) => {
                dispatch(changePasswordFailed());
                dispatch(addNotificationWithTimeout("error", "Cannot update your password!"));
            });
    };
};

export const confirmPassword = (email, currentPassword, recaptchaToken, onPasswordConfirmed) => {
    const userEmail = email.toLowerCase();

    return (dispatch) => {
        dispatch(confirmPasswordInProgress());

        fetch(`${IAM_API_URL}/auth`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                user_name: userEmail,
                password: currentPassword,
                remember_me: true,
                recaptcha_token: recaptchaToken
            })
        })
            .then((response) => {
                if (response.status === 201) {
                    return response.json();
                } else {
                    throw new Error("Login failed: invalid email or password!");
                }
            })
            .then((_) => {
                dispatch(confirmPasswordSuccessful(true));
                if (onPasswordConfirmed) {
                    onPasswordConfirmed();
                }
            })
            .catch((_) => {
                dispatch(confirmPasswordFailed(false));
                dispatch(addNotificationWithTimeout("error", "Your current password is incorrect"));
            });
    };
};

export const refreshAccessToken = () => {
    return (dispatch, getState) => {
        const refreshToken = getRefreshToken(getState());

        // Nothing to exchange. Sending the request anyway puts the word "null" in the
        // Authorization header, earns another 401, and starts the same round again.
        if (!refreshToken) {
            dispatch(logoutUser());
            return Promise.resolve(null);
        }

        const requestOptions = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${refreshToken}`
            }
        };

        dispatch(updateAccessTokenInProgress());

        return fetch(`${IAM_API_URL}/auth/refresh`, requestOptions)
            .then(
                (response) => (response.status === 200 || response.status === 201 ? response.json() : null),
                (_) => null
            )
            .then((body) => {
                const accessToken = body && body["access_token"];

                // A refresh token the endpoint refuses cannot be retried into working, so the
                // session ends here and the caller is told so.
                if (!accessToken) {
                    dispatch(updateAccessTokenFailed());
                    dispatch(logoutUser());
                    return null;
                }

                dispatch(updateAccessTokenSuccess(accessToken));

                const iam = {
                    access_token: accessToken,
                    refresh_token: refreshToken
                };

                setCookie(IAM_COOKIE_NAME, JSON.stringify(iam), defaultIamCookieAttributes);

                return accessToken;
            });
    };
};

export const updateRequiredExperiences = (value) => {
    return (dispatch, getState) => {
        dispatch(updateRequiredExperiencesInProgress());

        const userGuid = getLoggedInUserGuid(getState());
        const apiUrl = `${IAM_API_URL}/user/${userGuid}`;

        authRequest(apiUrl, dispatch, getState, {
            method: "PATCH",
            body: JSON.stringify({
                required_experiences: value
            })
        })
            .then((_) => {
                dispatch(updateRequiredExperiencesSuccessful(value));
            })
            .catch((_) => {
                dispatch(updateRequiredExperiencesFailed());
            });
    };
};

export const deleteUserAccount = (navigate) => {
    return (dispatch, getState) => {
        const apiUrl = `${IAM_API_URL}/user`;

        dispatch(deleteUserInProgress());

        authRequest(apiUrl, dispatch, getState, {
            method: "DELETE"
        })
            .then((_) => {
                dispatch(addNotificationWithTimeout("success", "Your account was successfully deleted!"));
                dispatch(deleteUserSuccessful());
                dispatch(logoutUser());
                navigate("/auth/login");
            })
            .catch((error) => {
                dispatch(
                    addNotificationWithTimeout(
                        "error",
                        "Cannot delete your account. Please try again or contact support!"
                    )
                );
                dispatch(deleteUserFailed());
            });
    };
};

export const createOrganizationIfNotExists = (name = "Default") => {
    return (dispatch, getState) => {
        dispatch(createOrganizationInProgress());

        const apiUrl = `${IAM_API_URL}/organization`;

        return authRequest(apiUrl, dispatch, getState, {
            method: "POST",
            body: JSON.stringify({
                name: name
            })
        })
            .then((response) => {
                dispatch(createOrganizationSuccessful(response));
                return Promise.resolve(response);
            })
            .catch((_) => {
                dispatch(createOrganizationFailed());
                return Promise.resolve({ error: "Cannot create organization" });
            });
    };
};

export const fetchOrganization = (showLoader = true) => {
    return (dispatch, getState) => {
        if (showLoader) {
            dispatch(getOrganizationInProgress());
        }

        const apiUrl = `${IAM_API_URL}/organization`;

        authRequest(apiUrl, dispatch, getState)
            .then((response) => {
                dispatch(getOrganizationSuccessful(response));
            })
            .catch((_) => {
                dispatch(getOrganizationFailed());
            });
    };
};

export const sendTeamMemberInvitation = (email) => {
    return (dispatch, getState) => {
        const apiUrl = `${IAM_API_URL}/organization/invite`;

        return authRequest(apiUrl, dispatch, getState, {
            method: "POST",
            body: JSON.stringify({
                email: email
            })
        });
    };
};

export const cancelTeamMemberInvitation = (invitationToken) => {
    return (dispatch, getState) => {
        dispatch(deleteTeamMemberInProgress(invitationToken));
        const apiUrl = `${IAM_API_URL}/organization/invite`;

        return authRequest(apiUrl, dispatch, getState, {
            method: "DELETE",
            body: JSON.stringify({
                token: invitationToken
            })
        })
            .then((_) => {
                dispatch(fetchOrganization(false));
                dispatch(deleteTeamMemberSuccessful(invitationToken));
            })
            .catch((_) => {
                dispatch(deleteTeamMemberFailed(invitationToken));
                dispatch(addNotificationWithTimeout("error", "Error on removing team member!"));
            });
    };
};

export const removeTeamMember = (email) => {
    return (dispatch, getState) => {
        dispatch(deleteTeamMemberInProgress(email));
        const apiUrl = `${IAM_API_URL}/organization/member`;

        return authRequest(apiUrl, dispatch, getState, {
            method: "DELETE",
            body: JSON.stringify({
                email: email
            })
        })
            .then((_) => {
                dispatch(fetchOrganization(false));
                dispatch(deleteTeamMemberSuccessful(email));
            })
            .catch((_) => {
                dispatch(deleteTeamMemberFailed(email));
                dispatch(addNotificationWithTimeout("error", "Error on removing team member!"));
            });
    };
};

export const updateOrganization = (payload) => {
    return (dispatch, getState) => {
        dispatch(updateOrganizationInProgress());

        const apiUrl = `${IAM_API_URL}/organization`;

        authRequest(apiUrl, dispatch, getState, {
            method: "PATCH",
            body: JSON.stringify(payload)
        })
            .then((response) => {
                dispatch(updateOrganizationSuccessful(response));
            })
            .catch((_) => {
                dispatch(updateOrganizationFailed());
            });
    };
};

export const registerOrganizationMember = (
    invitationToken,
    firstName,
    lastName,
    password,
    recaptchaToken,
    onError,
    navigate
) => {
    return (dispatch, getState) => {
        fetch(`${IAM_API_URL}/organization/member`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                f_name: firstName,
                l_name: lastName,
                pw: password,
                invitation_token: invitationToken,
                recaptcha_token: recaptchaToken,
                locale: "en_US",
                timezone: "America/New_York"
            })
        })
            .then((response) => {
                if (response.status === 201 || response.status === 409) {
                    return response.json();
                } else {
                    throw Error("Something went wrong during registration");
                }
            })
            .then((response) => {
                if (response["error"]) {
                    throw Error(response["error"]);
                }
                const iam = {
                    access_token: response["access_token"],
                    refresh_token: response["refresh_token"]
                };

                const serializedIAM = JSON.stringify(iam);

                if (iam.access_token && iam.refresh_token) {
                    setCookie(IAM_COOKIE_NAME, serializedIAM, defaultIamCookieAttributes);
                }
                return response;
            })
            .then((response) => {
                dispatch(authenticationSuccessful(response["access_token"], response["refresh_token"]));
                const subscription = getLoggedInUserSubscription(getState());

                if (subscription["plan_code"]) {
                    navigate("/dashboard");
                } else {
                    navigate("/free-trial-setup");
                }
            })
            .catch((error) => {
                dispatch(authenticationFailed());
                dispatch(addNotificationWithTimeout("error", error.message));
                onError();
            });
    };
};
